import { Check, ChevronDown, Plus } from 'lucide-react'
import { Select as SelectPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useFieldControl } from './field'
import { controlBase } from './input'

export interface SelectOption<V extends string = string> {
  value: V
  label: ReactNode
  /** Plain text used for type-ahead when the label is not a string. */
  textValue?: string
}

export interface SelectProps<V extends string = string> {
  value: V | undefined
  onValueChange: (value: V) => void
  options: readonly SelectOption<V>[]
  placeholder?: string
  size?: 'md' | 'sm'
  disabled?: boolean
  name?: string
  id?: string
  className?: string
  'aria-label'?: string
  onBlur?: () => void
  /** What the closed field shows instead of the chosen option's label (a compact form of it). */
  display?: ReactNode
  /**
   * Offers a "create" row at the end of the list (e.g. "New unit"): choosing it calls this instead of
   * changing the value, and the caller opens its quick-create form (`useQuickCreate`). Pass it only
   * when the user may create the record.
   */
  onCreate?: (() => void) | undefined
  createLabel?: string
}

/** The create row's value in a select or combobox list (never an id). */
export const CREATE_OPTION = '\u0000create'

/** The create row: after the options, set off by a rule, in the stamp colour. */
export const createItem =
  'mt-1 text-stamp before:absolute before:inset-x-1 before:-top-0.5 before:h-px before:bg-rule'

/**
 * A searchable picker's create row stays in view at the foot of a long list, so "New …" is there without
 * scrolling to the end. The rows scroll under it: it sits on the panel's background, flush with the
 * panel's foot (over the list's bottom padding) and with a band of it above for the rule, and its
 * highlight is laid over that rather than in its place (in some themes the tint is see-through). On the
 * item itself: cmdk moves its items while sorting a search, so no wrapper.
 */
export const stickyCreateItem =
  'sticky -bottom-1 z-10 bg-(--overlay-bg) shadow-[0_-0.25rem_0_var(--overlay-bg)] [backdrop-filter:var(--overlay-blur)] ' +
  'data-[selected=true]:bg-[linear-gradient(var(--stamp-tint),var(--stamp-tint))]'

/** On the list of a picker with a sticky create row: a row reached with the arrow keys scrolls clear of it. */
export const createListScroll = 'scroll-pb-[calc(var(--control-h)+0.25rem)]'

export const floatingPanel =
  'z-50 overflow-hidden rounded-panel border border-rule overlay text-ink animate-float-in'

/**
 * A row in a select, menu or picker list. Disabled is keyed on aria-disabled="true", not on
 * data-disabled: cmdk writes data-disabled="false" on every enabled item, which `data-disabled:`
 * (attribute present) would match, leaving no row clickable. Radix sets aria-disabled only when disabled.
 */
export const menuItem =
  'relative flex min-h-(--control-h) w-full cursor-default items-center gap-2 rounded-xs px-2 text-sm text-ink outline-none select-none ' +
  'data-highlighted:bg-stamp-tint aria-disabled:pointer-events-none aria-disabled:text-ink-faint [&_svg]:size-4 [&_svg]:shrink-0'

export function Select<V extends string = string>({
  value,
  onValueChange,
  options,
  placeholder,
  size = 'md',
  disabled,
  name,
  id,
  className,
  'aria-label': ariaLabel,
  onBlur,
  display,
  onCreate,
  createLabel,
}: SelectProps<V>) {
  const field = useFieldControl({ id })
  const canCreate = Boolean(onCreate && createLabel)
  return (
    <SelectPrimitive.Root
      // Always controlled: "" shows the placeholder (no option may use "" as its value).
      value={value ?? ''}
      // The create row keeps the value; the caller picks the record once it is created. "" is never an
      // option: it is Radix's hidden native select echoing a value the form set itself (before that
      // option was drawn), which must not reach the form as the user's change (it would mark it dirty).
      onValueChange={(v) =>
        v === CREATE_OPTION ? onCreate?.() : v === '' ? undefined : onValueChange(v as V)
      }
      disabled={disabled}
      name={name}
    >
      <SelectPrimitive.Trigger
        {...field}
        aria-label={ariaLabel}
        onBlur={onBlur}
        className={cn(
          controlBase,
          'inline-flex items-center justify-between gap-2 text-start data-placeholder:text-ink-faint',
          size === 'md' ? 'h-(--control-h) px-2.5 text-base' : 'h-7 px-2 text-sm',
          className,
        )}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder}>{display}</SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon className="text-ink-muted">
          <ChevronDown className="size-4" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            floatingPanel,
            'max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width)',
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                textValue={option.textValue}
                className={cn(menuItem, 'pe-7')}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute end-2 flex text-stamp">
                  <Check aria-hidden />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
            {canCreate ? (
              <SelectPrimitive.Item
                value={CREATE_OPTION}
                textValue={createLabel}
                className={cn(menuItem, createItem, options.length === 0 && 'mt-0 before:hidden')}
              >
                <Plus aria-hidden />
                <SelectPrimitive.ItemText>{createLabel}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ) : null}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
